import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  Snowflake,
  Play,
  RefreshCw,
  User,
  Calendar,
  ExternalLink,
  X,
  CreditCard,
  ShieldAlert,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const AdminDisputesSection = () => {
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchUser, setSearchUser] = useState('');
  
  // Freeze/Unfreeze state
  const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);
  const [freezeUserId, setFreezeUserId] = useState('');
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeLoading, setFreezeLoading] = useState(false);
  
  const [unfreezeUserId, setUnfreezeUserId] = useState(null);
  const [unfreezeLoading, setUnfreezeLoading] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchUser) {
        params.append('user_id_filter', searchUser);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/disputes/?${queryString}` : '/disputes/';
      const response = await apiClient.get(url);
      setDisputes(response.data.disputes || []);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching disputes:', err);
      setError('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, [statusFilter]);

  const handleFreeze = async () => {
    if (!freezeUserId.trim() || !freezeReason.trim()) {
      toast.error('Please provide user ID and reason');
      return;
    }
    
    setFreezeLoading(true);
    try {
      await apiClient.post('/disputes/manual-freeze', {
        user_id: freezeUserId.trim(),
        reason: freezeReason.trim()
      });
      
      toast.success('User payouts frozen successfully');
      setFreezeDialogOpen(false);
      setFreezeUserId('');
      setFreezeReason('');
      fetchDisputes();
    } catch (err) {
      console.error('Error freezing payouts:', err);
      toast.error(err.response?.data?.detail || 'Failed to freeze payouts');
    } finally {
      setFreezeLoading(false);
    }
  };

  const handleUnfreeze = async (userId) => {
    setUnfreezeLoading(true);
    try {
      await apiClient.post(`/disputes/unfreeze/${userId}`);
      toast.success('User payouts unfrozen successfully');
      setUnfreezeUserId(null);
      fetchDisputes();
    } catch (err) {
      console.error('Error unfreezing payouts:', err);
      toast.error(err.response?.data?.detail || 'Failed to unfreeze payouts');
    } finally {
      setUnfreezeLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'open': { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'pending': { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'under_review': { color: 'bg-blue-500/20 text-blue-400', icon: AlertCircle },
      'needs_response': { color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
      'won': { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'lost': { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    };
    
    const config = statusConfig[status] || { color: 'bg-slate-500/20 text-muted-foreground', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchDisputes} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-disputes-section">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-card">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Disputes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-500">{stats.open}</p>
                <p className="text-xs text-muted-foreground">Open</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">{stats.won}</p>
                <p className="text-xs text-muted-foreground">Won</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">{stats.lost}</p>
                <p className="text-xs text-muted-foreground">Lost</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-lg font-bold text-purple-500">{formatCurrency(stats.total_amount_disputed)}</p>
                <p className="text-xs text-muted-foreground">Total Disputed</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions Bar */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-3">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="needs_response">Needs Response</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              
              {/* User Search */}
              <div className="flex gap-2">
                <Input
                  placeholder="User ID..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  className="w-[200px]"
                />
                <Button variant="outline" onClick={fetchDisputes}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Freeze Payout Button */}
            <Button 
              variant="destructive" 
              onClick={() => setFreezeDialogOpen(true)}
              data-testid="freeze-payout-btn"
            >
              <Snowflake className="w-4 h-4 mr-2" />
              Freeze User Payout
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Disputes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Disputes & Chargebacks
            </span>
            <Button variant="ghost" size="sm" onClick={fetchDisputes}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {disputes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-500" />
              <p>No disputes found matching the criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 font-medium">Dispute ID</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Amount</th>
                    <th className="text-left p-3 font-medium">Reason</th>
                    <th className="text-left p-3 font-medium">Creator</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {disputes.map((dispute) => (
                    <tr 
                      key={dispute.id} 
                      className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                      data-testid={`admin-dispute-row-${dispute.id}`}
                      onClick={() => setSelectedDispute(dispute)}
                    >
                      <td className="p-3">
                        <span className="font-mono text-xs">{dispute.id}</span>
                        {dispute.stripe_dispute_id && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Stripe: {dispute.stripe_dispute_id.substring(0, 12)}...
                          </p>
                        )}
                      </td>
                      <td className="p-3">
                        {getStatusBadge(dispute.status)}
                      </td>
                      <td className="p-3 font-semibold text-red-500">
                        {formatCurrency(dispute.amount)}
                      </td>
                      <td className="p-3 capitalize">
                        {dispute.reason?.replace('_', ' ')}
                      </td>
                      <td className="p-3">
                        {dispute.creator_id ? (
                          <span className="font-mono text-xs">{dispute.creator_id.substring(0, 12)}...</span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {formatDate(dispute.created_at)}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {dispute.creator_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setUnfreezeUserId(dispute.creator_id)}
                              className="text-green-500 hover:bg-green-500/10"
                              data-testid={`unfreeze-${dispute.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Unfreeze
                            </Button>
                          )}
                          {dispute.campaign_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/campaign/${dispute.campaign_id}`, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Freeze Payout Dialog */}
      <Dialog open={freezeDialogOpen} onOpenChange={setFreezeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-blue-500" />
              Freeze User Payouts
            </DialogTitle>
            <DialogDescription>
              This will prevent the user from receiving any payouts until unfrozen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">User ID</label>
              <Input
                value={freezeUserId}
                onChange={(e) => setFreezeUserId(e.target.value)}
                placeholder="Enter user ID..."
                data-testid="freeze-user-id-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Textarea
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                placeholder="Provide reason for freezing payouts..."
                data-testid="freeze-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFreezeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFreeze}
              disabled={freezeLoading || !freezeUserId.trim() || !freezeReason.trim()}
              data-testid="confirm-freeze-btn"
            >
              {freezeLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Snowflake className="w-4 h-4 mr-2" />
              )}
              Freeze Payouts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unfreeze Confirmation Dialog */}
      <Dialog open={!!unfreezeUserId} onOpenChange={() => setUnfreezeUserId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-500" />
              Unfreeze User Payouts
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to unfreeze payouts for this user? This will allow them to receive payouts again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnfreezeUserId(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUnfreeze(unfreezeUserId)}
              disabled={unfreezeLoading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-unfreeze-btn"
            >
              {unfreezeLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Unfreeze Payouts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Detail Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedDispute(null)}>
          <div 
            className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-dispute-detail-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Dispute Details (Admin)</h3>
                <p className="text-xs text-muted-foreground font-mono">{selectedDispute.id}</p>
              </div>
              <button onClick={() => setSelectedDispute(null)} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status & Amount */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedDispute.status)}
                  <Badge variant="outline" className="capitalize">{selectedDispute.type || 'chargeback'}</Badge>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-red-500">{formatCurrency(selectedDispute.amount)}</p>
                  <p className="text-xs text-muted-foreground uppercase">{selectedDispute.currency || 'USD'}</p>
                </div>
              </div>

              {/* Detail Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Reason</p>
                    <p className="font-medium text-foreground capitalize mt-1">{(selectedDispute.reason || 'Unknown').replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><CreditCard className="w-3 h-3" /> Payment Reference</p>
                    <p className="font-mono text-xs text-foreground mt-1">{selectedDispute.payment_intent_id || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Stripe Dispute ID</p>
                    <p className="font-mono text-xs text-foreground mt-1">{selectedDispute.stripe_dispute_id || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Creator ID</p>
                    <p className="font-mono text-xs text-foreground mt-1">{selectedDispute.creator_id || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {(selectedDispute.donor_name || selectedDispute.donor_email) && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Donor</p>
                      <p className="font-medium text-foreground mt-1">{selectedDispute.donor_name || 'Unknown'}</p>
                      {selectedDispute.donor_email && (
                        <p className="text-xs text-muted-foreground">{selectedDispute.donor_email}</p>
                      )}
                    </div>
                  )}
                  {selectedDispute.campaign_id && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Campaign</p>
                      <a href={`/campaign/${selectedDispute.campaign_id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-400 hover:underline mt-1 block font-mono">
                        {selectedDispute.campaign_id} <ExternalLink className="w-3 h-3 inline ml-1" />
                      </a>
                    </div>
                  )}
                  {selectedDispute.evidence_due_by && selectedDispute.status !== 'won' && selectedDispute.status !== 'lost' && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-xs text-yellow-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Evidence Due By</p>
                      <p className="font-medium text-yellow-400 mt-1">{formatDate(selectedDispute.evidence_due_by)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Timeline
                </h4>
                <div className="space-y-3 pl-4 border-l-2 border-border">
                  <div className="relative">
                    <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-red-500 border-2 border-card" />
                    <p className="text-sm text-foreground">Dispute opened</p>
                    <p className="text-xs text-muted-foreground">{new Date(selectedDispute.created_at).toLocaleString()}</p>
                  </div>
                  {selectedDispute.updated_at && selectedDispute.updated_at !== selectedDispute.created_at && (
                    <div className="relative">
                      <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-blue-500 border-2 border-card" />
                      <p className="text-sm text-foreground">Last updated</p>
                      <p className="text-xs text-muted-foreground">{new Date(selectedDispute.updated_at).toLocaleString()}</p>
                    </div>
                  )}
                  {(selectedDispute.status === 'won' || selectedDispute.status === 'lost') && (
                    <div className="relative">
                      <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 border-card ${selectedDispute.status === 'won' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <p className="text-sm text-foreground">Dispute {selectedDispute.status}</p>
                      <p className="text-xs text-muted-foreground">{new Date(selectedDispute.updated_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Actions */}
              <div className="flex gap-3 pt-2 border-t border-border">
                {selectedDispute.creator_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setUnfreezeUserId(selectedDispute.creator_id); setSelectedDispute(null); }}
                    className="text-green-500 hover:bg-green-500/10"
                  >
                    <Play className="w-3 h-3 mr-1" /> Unfreeze User Payouts
                  </Button>
                )}
                {selectedDispute.campaign_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/campaign/${selectedDispute.campaign_id}`, '_blank')}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> View Campaign
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisputesSection;
